/**
 * Masthead. Heritage-newspaper header with seal and current owner mark.
 */
import { store } from '../store.ts';
import { withVault, injectCommonStyles } from './_shared.ts';
import { currentEntitlement, PRICING } from '../chassis/payment.ts';

const TPL = /* html */ `
<style>
.masthead {
  padding-top: 28px;
  padding-bottom: 6px;
  display: grid;
  gap: 6px;
}
.top {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  font-family: var(--mono);
  font-size: 9.5px;
  letter-spacing: 0.36em;
  text-transform: uppercase;
  color: var(--ink-dim);
  padding-bottom: 16px;
  border-bottom: 1px solid var(--ink);
  position: relative;
}
.top::after {
  content: '';
  position: absolute;
  left: 0; right: 0; bottom: -4px;
  height: 1px;
  background: var(--indigo);
  opacity: 0.4;
}
.top .center { display: flex; align-items: center; gap: 8px; justify-content: center; }
.top .dot { width: 4px; height: 4px; background: var(--indigo); border-radius: 50%; display: inline-block; }
.mark {
  font-family: var(--hindi);
  font-size: clamp(64px, 16vw, 96px);
  color: var(--indigo);
  line-height: 0.9;
  letter-spacing: -0.02em;
  text-align: center;
  margin-top: 16px;
}
.en {
  font-family: var(--display);
  font-style: italic;
  font-weight: 600;
  font-size: clamp(20px, 5vw, 28px);
  color: var(--ink);
  text-align: center;
  margin-top: -4px;
}
.en::before, .en::after {
  content: '·';
  color: var(--indigo);
  margin: 0 12px;
  opacity: 0.7;
}
.owner {
  text-align: center;
  font-family: var(--display);
  font-style: italic;
  font-size: 17px;
  color: var(--ink-dim);
  margin-top: 4px;
}
.owner .name { color: var(--indigo); font-weight: 600; font-style: normal; }
.tier-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 8px auto 0;
  padding: 4px 12px;
  background: var(--indigo);
  color: var(--parchment);
  font-family: var(--mono);
  font-size: 9.5px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  border-radius: 99px;
}
.tier-pill.free {
  background: transparent;
  color: var(--ink-dim);
  border: 1px solid var(--rule-strong);
}
.seal-row {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}
.seal {
  width: 88px;
  height: 88px;
  transform: rotate(-3deg);
  filter: drop-shadow(0 1px 0 oklch(40% 0.040 50 / 0.08));
}
</style>
<header class="masthead">
  <div class="top">
    <span>Vol. I</span>
    <span class="center">वसीयत<span class="dot"></span>The Family Vault<span class="dot"></span>Issue I</span>
    <span id="date"></span>
  </div>
  <div class="mark">वसीयत</div>
  <div class="en">Wasiyat</div>
  <div class="owner">For the records of <span class="name" id="owner-name">—</span></div>
  <div style="display:flex;justify-content:center;"><div class="tier-pill" id="tier-pill">FREE</div></div>
  <div class="seal-row">
    <svg class="seal" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <path id="seal-arc-top" d="M 50,50 m -38,0 a 38,38 0 0,1 76,0" />
        <path id="seal-arc-bot" d="M 50,50 m -38,0 a 38,38 0 0,0 76,0" />
      </defs>
      <circle cx="50" cy="50" r="46" fill="none" stroke="var(--indigo)" stroke-width="1" opacity="0.85" />
      <circle cx="50" cy="50" r="40" fill="none" stroke="var(--indigo)" stroke-width="0.6" opacity="0.6" />
      <circle cx="50" cy="50" r="30" fill="none" stroke="var(--indigo)" stroke-width="0.4" opacity="0.4" />
      <text class="seal-text" font-family="var(--mono)" font-size="6" letter-spacing="2.4" fill="var(--indigo)">
        <textPath href="#seal-arc-top" startOffset="50%" text-anchor="middle">VERIFIED · WASIYAT</textPath>
      </text>
      <text class="seal-text" font-family="var(--mono)" font-size="6" letter-spacing="2.4" fill="var(--indigo)">
        <textPath href="#seal-arc-bot" startOffset="50%" text-anchor="middle">MMXXVI · I</textPath>
      </text>
      <text x="50" y="58" text-anchor="middle" font-family="var(--hindi)" font-size="28" fill="var(--indigo)">व</text>
    </svg>
  </div>
</header>
`;

export class WasiyatMasthead extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub: (() => void) | null = null;

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    injectCommonStyles(this.#shadow);
    const date = this.#shadow.getElementById('date');
    if (date) date.textContent = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
    this.#unsub = withVault(this, (v, root) => {
      const owner = root.getElementById('owner-name');
      if (owner) owner.textContent = v.owner.display_name;
      const pill = root.getElementById('tier-pill') as HTMLElement | null;
      if (pill) {
        const ent = currentEntitlement();
        const conf = PRICING.find((p) => p.id === ent.tier);
        pill.textContent = conf?.name.toUpperCase() ?? 'FREE';
        pill.classList.toggle('free', ent.tier === 'free');
      }
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }
}

customElements.define('wasiyat-masthead', WasiyatMasthead);

const _ref = store;
void _ref;
